import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  DataTableCellContent,
  DataTableCellTrigger,
  DataTablePopoverCell,
  createColumnHelper,
  Box,
  Icon,
  Text,
  type DataTableColumnDef,
} from "@salestrak/ui";
import type { IssueRow, StageOption } from "#app/lib/data";
import type { WorkspaceWorkflow } from "#app/lib/data";
import {
  EntityGroupTag,
  EntitySelectionHover,
  EntityTag,
  useEntityDetail,
  type EntityData,
} from "#app/components/entity";
import { entityFromAssigneeRef } from "#app/lib/entity-visuals";
import { StageSelector, AssigneeSelector } from "#app/components/entity-selectors";
import { AgeLabel } from "#app/components/age/age-label";
import { AgeHistoryPopover } from "#app/components/age/age-history-popover";
import { AddressValueCell } from "#app/components/fields/address-value-cell";
import { canEdit } from "#app/lib/utils/access-level";
import type { Omittable } from "@salestrak/ui";

/**
 * Canonical issue column defs for the shared {@link IssuesTable} (used at every
 * scope — workflow, team, member). Consumers pass the output of
 * `useIssueColumns` to `<DataTable columns=>`. All entity cells go through
 * `#app/components/entity` — never hand-rolled avatar/label pairs.
 */

const h = createColumnHelper<IssueRow>();

// Inline cell wrappers — draft-on-close commit with domain selectors.

function DraftPopoverCell({
  issueId,
  entityIds,
  onCommit,
  onSetPrimary,
  entities,
  triggerContent,
  renderSelector,
}: Omittable<{
  issueId: string;
  entityIds: string[];
  onCommit: (issueId: string, ids: string[]) => void;
  onSetPrimary?: (issueId: string, entityId: string) => void;
  entities?: EntityData[];
  /** Receives whether the picker popover is open, so the trigger chip can
   *  suppress its hover preview while the picker is up. */
  triggerContent?: (pickerOpen: boolean) => React.ReactNode;
  renderSelector: (
    value: string[],
    onValueChange: (ids: string[]) => void,
    onSetPrimary?: (entityId: string) => void,
    close?: () => void,
  ) => React.ReactNode;
}>) {
  const draftRef = useRef(entityIds);
  const [draft, setDraft] = useState(entityIds);
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setPickerOpen(open);
      if (open) {
        draftRef.current = entityIds;
        setDraft(entityIds);
      } else {
        const prev = entityIds;
        const next = draftRef.current;
        const changed =
          prev.length !== next.length || prev.some((id, i) => id !== next[i]);
        if (changed) {
          onCommit(issueId, draftRef.current);
        }
      }
    },
    [issueId, entityIds, onCommit],
  );

  const handleValueChange = useCallback((ids: string[]) => {
    draftRef.current = ids;
    setDraft(ids);
  }, []);

  return (
    <DataTablePopoverCell
      onOpenChange={handleOpenChange}
      contentProps={{
        align: "end",
        padding: "none",
        className: "w-96",
      }}
      content={(close) =>
        renderSelector(
          draft,
          handleValueChange,
          onSetPrimary
            ? (entityId: string) => onSetPrimary(issueId, entityId)
            : undefined,
          close,
        )
      }
    >
      {entityIds.length === 0 ? (
        <Box width={24} height={24} align="center" justify="center">
          <Icon name="user-plus" size="md" color="muted" />
        </Box>
      ) : triggerContent ? (
        triggerContent(pickerOpen)
      ) : (
        <EntityGroupTag
          entities={entities ?? []}
          size="sm"
          interactive={false}
          hoverDisabled={pickerOpen}
        />
      )}
    </DataTablePopoverCell>
  );
}

function AssigneePopoverCell({
  row,
  onAssigneesChange,
}: {
  row: IssueRow;
  onAssigneesChange: (issueId: string, entityIds: string[]) => void;
}) {
  const { openEntity } = useEntityDetail();

  const entityIds = useMemo(
    () => row.assignees.map((r) => r.entityId),
    [row.assignees],
  );
  const entities = useMemo(
    () => row.assignees.map(entityFromAssigneeRef),
    [row.assignees],
  );

  return (
    <DraftPopoverCell
      issueId={row.issueId}
      entityIds={entityIds}
      onCommit={onAssigneesChange}
      onSetPrimary={(issueId, entityId) => {
        onAssigneesChange(issueId, [
          entityId,
          ...entityIds.filter((id) => id !== entityId),
        ]);
      }}
      entities={entities}
      triggerContent={
        row.assignees.length > 0
          ? (pickerOpen) => (
              <EntityGroupTag
                entities={entities}
                variant="expanded"
                size="sm"
                title="Assignees"
                interactive={false}
                hoverDisabled={pickerOpen}
              />
            )
          : undefined
      }
      renderSelector={(value, onChange, onSetPrimary, close) => (
        <AssigneeSelector
          variant="inline"
          mode="list"
          value={value}
          selectedRefs={row.assignees}
          onValueChange={onChange}
          onSetPrimary={onSetPrimary}
          onViewProfile={(userId) => {
            close?.();
            openEntity("user", userId);
          }}
        />
      )}
    />
  );
}

function StagePopoverCell({
  row,
  stage,
  onChangeStage,
}: Omittable<{
  row: IssueRow;
  stage?: StageOption;
  onChangeStage: (issueId: string, stageId: string) => void;
}>) {
  // Track the picker so the chip's workflow hover preview stays down while
  // the stage picker popover is up (same suppression as the assignee cell).
  const [pickerOpen, setPickerOpen] = useState(false);

  const entity = useMemo<EntityData | null>(
    () =>
      stage
        ? {
            kind: "stage",
            id: stage.stageId,
            stage,
            workflowId: row.workflowId,
          }
        : null,
    [stage, row.workflowId],
  );

  return (
    <DataTablePopoverCell
      onOpenChange={setPickerOpen}
      contentProps={{ padding: "none" }}
      content={(close) => (
        <StageSelector
          variant="inline"
          workflowId={row.workflowId}
          value={row.stageId}
          onValueChange={(stageId) => {
            onChangeStage(row.issueId, stageId);
            close();
          }}
        />
      )}
    >
      <Box direction="row" align="center" gap="sm" overflow="hidden" fill="flex">
        {entity ? (
          <EntitySelectionHover
            entities={[entity]}
            title="Stage"
            disabled={pickerOpen}
          >
            <Box shrink>
              <EntityTag entity={entity} weight="regular" interactive={false} />
            </Box>
          </EntitySelectionHover>
        ) : (
          <Text size="sm" truncate={1}>
            Unknown
          </Text>
        )}
      </Box>
    </DataTablePopoverCell>
  );
}

function AddressCell({
  issue,
  onAddressSelect,
  onAddressClear,
}: {
  issue: IssueRow;
  onAddressSelect: (issue: IssueRow, wholeAddress: string) => void;
  onAddressClear: (issue: IssueRow) => void;
}) {
  const handleSelect = useCallback(
    (wholeAddress: string) => onAddressSelect(issue, wholeAddress),
    [onAddressSelect, issue],
  );
  const handleClear = useCallback(
    () => onAddressClear(issue),
    [onAddressClear, issue],
  );

  return (
    <AddressValueCell
      wholeAddress={issue.address?.wholeAddress}
      onSelect={handleSelect}
      onClear={handleClear}
      readOnly={!canEdit(issue.viewerAccessLevel)}
    />
  );
}

export type UseIssueColumnsParams = Omittable<{
  availableStages: StageOption[];
  workflows?: WorkspaceWorkflow[];
  onOpenIssue: (issue: IssueRow) => void;
  onChangeStage: (issueId: string, stageId: string) => void;
  onAssigneesChange?: (issueId: string, entityIds: string[]) => void;
  onAgePress: (issue: IssueRow) => void;
  /** Address editing is optional — when either handler is absent the Address
   *  column is omitted entirely (e.g. the dashboard `entity-list` widget, which
   *  never shows it). */
  onAddressSelect?: (issue: IssueRow, wholeAddress: string) => void;
  onAddressClear?: (issue: IssueRow) => void;
}>;

export function useIssueColumns({
  availableStages,
  workflows,
  onOpenIssue,
  onChangeStage,
  onAssigneesChange,
  onAgePress,
  onAddressSelect,
  onAddressClear,
}: UseIssueColumnsParams): DataTableColumnDef<IssueRow>[] {
  const stageById = useMemo(() => {
    const map = new Map<string, StageOption>();
    for (const s of availableStages) map.set(s.stageId, s);
    return map;
  }, [availableStages]);

  return useMemo<DataTableColumnDef<IssueRow>[]>(
    () => [
      h.hoverAction({
        accessorKey: "name",
        header: "Name",
        enableSorting: true,
        content: (issue) => (
          <EntityTag
            entity={{
              kind: "issue",
              id: issue.issueId,
              issue: { name: issue.name, icon: issue.icon },
              workflowId: issue.workflowId,
            }}
            display="label"
            weight="regular"
            interactive={false}
          />
        ),
        onClickAction: (issue) => onOpenIssue(issue),
        actionLabel: (issue) => `Open ${issue.name}`,
      }),

      h.display({
        id: "stage",
        header: "Stage",
        enableSorting: true,
        meta: { sortId: "stage.sortOrder", minWidth: 160, maxWidth: 200 },
        cell: ({ row }) => (
          <StagePopoverCell
            row={row.original}
            stage={stageById.get(row.original.stageId)}
            onChangeStage={onChangeStage}
          />
        ),
      }),

      h.display({
        id: "age",
        header: "Age",
        cell: ({ row }) => (
          // Same hover preview as the kanban card's age button; click still
          // opens the update-age dialog via onAgePress.
          <AgeHistoryPopover issue={row.original}>
            <DataTableCellTrigger onClick={() => onAgePress(row.original)}>
              <DataTableCellContent>
                <AgeLabel age={row.original.age} />
              </DataTableCellContent>
            </DataTableCellTrigger>
          </AgeHistoryPopover>
        ),
      }),

      h.display({
        id: "assignees",
        header: "Assignees",
        meta: { minWidth: 200 },
        cell: ({ row }) =>
          onAssigneesChange ? (
            <AssigneePopoverCell
              row={row.original}
              onAssigneesChange={onAssigneesChange}
            />
          ) : (
            <DataTableCellContent>
              <EntityGroupTag
                entities={row.original.assignees.map(entityFromAssigneeRef)}
                variant="expanded"
                size="sm"
                title="Assignees"
              />
            </DataTableCellContent>
          ),
      }),

      ...(onAddressSelect && onAddressClear
        ? [
            h.display({
              id: "address",
              header: "Address",
              meta: { minWidth: 250 },
              cell: ({ row }) => (
                <AddressCell
                  issue={row.original}
                  onAddressSelect={onAddressSelect}
                  onAddressClear={onAddressClear}
                />
              ),
            }),
          ]
        : []),

      h.date({ accessorKey: "creationDate", header: "Created" }),
      h.date({ accessorKey: "lastUpdateDate", header: "Updated" }),
    ],
    [
      stageById,
      onOpenIssue,
      onChangeStage,
      onAssigneesChange,
      onAgePress,
      onAddressSelect,
      onAddressClear,
    ],
  );
}
